import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Gift, RefreshCw, Loader2, Wallet, ArrowUpCircle, History } from 'lucide-react';
import { vendorAdminApi } from '../services/api';
import { useVendorContext } from './VendorAdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const money = (v) => `CA$${Number(v || 0).toFixed(2)}`;
const dt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const VendorGiftPoolPage = () => {
  const { vendor_id, impersonating } = useVendorContext();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState(null);
  const [amount, setAmount] = useState('50');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await vendorAdminApi.getGiftPool(impersonating ? vendor_id : undefined);
      setState(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load gift pool');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendor_id]);

  // On return from Stripe redirect, finalize the session
  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) return;
    (async () => {
      try {
        const r = await vendorAdminApi.finalizeGiftPoolTopup(sid, impersonating ? vendor_id : undefined);
        if (r?.ok) {
          toast.success(`Gift pool topped up ${money(r.credited)}`);
        } else if (r?.payment_status === 'unpaid') {
          toast.error('Payment was not completed.');
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Finalize failed');
      } finally {
        params.delete('session_id');
        setParams(params, { replace: true });
        load();
      }
    })();
    // eslint-disable-next-line
  }, []);

  const topUp = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 10) {
      toast.error('Minimum top-up is CA$10.00');
      return;
    }
    setBusy(true);
    try {
      const r = await vendorAdminApi.createGiftPoolTopupSession(
        amt, window.location.origin, impersonating ? vendor_id : undefined,
      );
      if (r?.url) {
        window.location.href = r.url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start top-up');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Gift className="h-5 w-5 text-emerald-600" /> Gift pool
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl text-sm">
            Pre-fund a store-credit balance customers can spend at your storefront. Every dollar you
            load reduces the goods invoice on future orders — think of it as prepaid promotional
            credit for your regulars.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="gift-pool-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Balance card */}
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Current pool balance
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span
              className="text-4xl font-semibold text-slate-900"
              data-testid="gift-pool-balance"
            >
              {loading ? '—' : money(state?.balance_cad)}
            </span>
            <span className="text-sm text-slate-500">CAD</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Customers see this as available store credit at your checkout and can redeem it towards
            the goods portion of their order.
          </p>
        </div>

        {/* Top up */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
            <ArrowUpCircle className="h-3.5 w-3.5" /> Top up your pool
          </div>
          <div className="mt-3 flex items-center gap-2">
            {[25, 50, 100, 250].map((v) => (
              <Button
                key={v}
                variant={String(v) === amount ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAmount(String(v))}
                data-testid={`gift-pool-quick-${v}`}
              >
                CA${v}
              </Button>
            ))}
          </div>
          <div className="mt-3 space-y-1">
            <Label htmlFor="gift-pool-amount">Amount (CAD)</Label>
            <Input
              id="gift-pool-amount"
              data-testid="gift-pool-amount-input"
              type="number"
              min="10"
              max="5000"
              step="5"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <p className="text-[11px] text-slate-400">Between CA$10 and CA$5,000 per top-up.</p>
          </div>
          <Button
            className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={topUp}
            disabled={busy}
            data-testid="gift-pool-topup-submit"
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wallet className="mr-1.5 h-4 w-4" />}
            Pay with card
          </Button>
        </div>
      </div>

      {/* Ledger */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 text-sm font-medium text-slate-700">
          <History className="h-4 w-4" /> Activity
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>
        ) : (state?.ledger || []).length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm" data-testid="gift-pool-ledger-empty">
            No activity yet.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium px-4 py-2">Date</th>
                <th className="text-left font-medium px-4 py-2">Type</th>
                <th className="text-left font-medium px-4 py-2">Reason</th>
                <th className="text-right font-medium px-4 py-2">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(state?.ledger || []).map((row, i) => (
                <tr key={row.id || `${row.created_at}-${i}`} data-testid={`gift-pool-ledger-row-${i}`}>
                  <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{dt(row.created_at)}</td>
                  <td className="px-4 py-2 capitalize">
                    {row.type === 'credit' ? (
                      <span className="text-emerald-700 font-medium">+ Credit</span>
                    ) : (
                      <span className="text-slate-700">− Debit</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-600 capitalize">
                    {(row.reason || row.type).replace(/_/g, ' ')}
                    {row.order_id ? <span className="text-xs text-slate-400 ml-1">· {row.order_id}</span> : null}
                  </td>
                  <td className="px-4 py-2 text-right font-medium">{money(row.amount_cad)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default VendorGiftPoolPage;
